export const LANGUAGES = ['en', 'ko', 'ja', 'zh', 'es', 'fr', 'de', 'pt', 'ru'] as const

export type Language = (typeof LANGUAGES)[number]

export function isLanguage(value: string): value is Language {
  return (LANGUAGES as readonly string[]).includes(value)
}

/** 브라우저 언어의 앞부분만 본다 — zh-Hant 든 zh-CN 이든 zh 로 묶는다. */
export function getSystemLanguage(): Language {
  if (typeof navigator === 'undefined') return 'en'
  const prefix = navigator.language.toLowerCase().split('-')[0]
  return isLanguage(prefix) ? prefix : 'en'
}

/**
 * 주소의 첫 칸에서 언어를 읽는다 — `/ja/` · `/ja` 둘 다 `ja`.
 *
 * **왜 주소가 언어를 정하나**: 검색엔진은 `navigator.language` 를 흉내 내지 않는다. 언어가
 * 브라우저 설정으로만 정해지면 URL 이 하나뿐이라 **어느 언어로도 따로 순위에 오를 수 없다.**
 * 2026-08-11 실측: 카피는 9개 언어인데 sitemap 은 URL 1개, hreflang 0개, `<title>` 은 한국어
 * 고정이었다. 그래서 언어마다 주소를 주고 그 주소가 언어의 정본이 된다.
 */
export function getUrlLanguage(pathname: string): Language | null {
  const first = pathname.split('/').filter(Boolean)[0]
  return first !== undefined && isLanguage(first) ? first : null
}

/**
 * 첫 화면에 쓸 언어. **주소가 가장 세다** — `/ja/` 를 열었으면 브라우저가 한국어여도 일본어다.
 * 주소에 언어가 없을 때(`/` = x-default)만 저장된 선택 → 브라우저 설정 순으로 내려간다.
 */
export function resolveInitialLanguage(): Language {
  if (typeof window === 'undefined') return 'en'
  const fromUrl = getUrlLanguage(window.location.pathname)
  if (fromUrl !== null) return fromUrl

  const saved = window.localStorage.getItem('language')
  if (saved !== null && isLanguage(saved)) return saved
  return getSystemLanguage()
}

/**
 * 언어를 바꾸면 주소도 같이 바꾼다. 새로고침·공유·뒤로가기가 전부 그 언어를 기억하고,
 * 검색엔진이 본 주소와 사람이 보는 화면이 어긋나지 않는다. 페이지는 다시 불러오지 않는다.
 */
export function languageHref(language: Language): string {
  return `/${language}/`
}
